import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { apiClient } from "../../api/client";

type GlobalAiConfig = {
  smallModel: string;
  largeModel: string;
  veryLargeModel: string;
  imageModel: string;
};

type AotdListItem = {
  _id: string;
  date: string;
  title?: string;
  createdBy?: string;
  imageModel?: string | null;
  generationStatus?: {
    status?: string;
    state?: string;
  };
};

type PipelineStep = {
  name?: string;
  model?: string;
  durationMs?: number;
  /** prompt sent to the model (input) */
  promptSent?: string;
  /** raw response from the model (output) */
  rawResponse?: string;
};

type AotdDetails = {
  _id: string;
  date: string;
  title?: string;
  createdBy?: string;
  prompt?: string;
  imageModel?: string | null;
  generationStatus?: {
    status?: string;
    state?: string;
  };
  pipelineMetadata?: {
    emojis?: string[];
    concept?: string;
    artStyle?: string;
    critique?: string;
    imagePrompt?: string;
    steps?: PipelineStep[];
    totalDurationMs?: number;
  };
  metadata?: {
    pipeline?: {
      emojis?: string[];
      concept?: string;
      artStyle?: string;
      critique?: string;
      imagePrompt?: string;
      steps?: PipelineStep[];
      totalDurationMs?: number;
    };
  };
};

const Page = styled.div`
  min-height: 100vh;
  background: #c9febe;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  margin: 0 0 18px;
  color: #355;
  font-size: 0.95rem;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.82rem;
  color: #444;
  margin: 10px 0 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #cfd8dc;
  font-size: 0.92rem;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.9rem;
  cursor: pointer;
  background: #1f6feb;
  color: #fff;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostButton = styled(Button)`
  background: #f0f3f5;
  color: #223;
`;

const StatusText = styled.div`
  margin-top: 10px;
  font-size: 0.85rem;
  color: #334;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const ListItem = styled.button<{ active?: boolean }>`
  text-align: left;
  border: 1px solid ${(p) => (p.active ? "#1f6feb" : "#dce3e8")};
  background: ${(p) => (p.active ? "#eff6ff" : "#fff")};
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
`;

const SmallText = styled.div`
  color: #556;
  font-size: 0.82rem;
`;

const KeyValue = styled.div`
  margin: 8px 0;
  font-size: 0.9rem;
`;

const StepBlock = styled.div`
  margin-top: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fafbfc;
`;

const StepHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: #f1f5f9;
  font-size: 0.9rem;
  flex-wrap: wrap;
`;

const StepDetail = styled.span`
  color: #475569;
`;

const StepInputSummary = styled.summary`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #334;
  background: #f8fafc;
  border-top: 1px solid #eef2f4;
  user-select: none;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const StepInputBody = styled.pre`
  margin: 0;
  padding: 12px;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 280px;
  overflow: auto;
  background: #fff;
  border-top: 1px solid #eef2f4;
  color: #334;
`;

const StepOutputLabel = styled.div`
  padding: 6px 12px;
  font-size: 0.8rem;
  color: #475569;
  background: #f1f5f9;
  border-top: 1px solid #eef2f4;
`;

const StepOutputBody = styled.pre`
  margin: 0;
  padding: 12px;
  font-size: 0.82rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow: auto;
  background: #fff;
  color: #334;
`;

const EmptyState = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed #c5d0d8;
  border-radius: 8px;
  color: #566;
  font-size: 0.9rem;
`;

const defaultConfig: GlobalAiConfig = {
  smallModel: "",
  largeModel: "",
  veryLargeModel: "",
  imageModel: "",
};

function formatDate(value?: string) {
  if (!value) return "unknown";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

const AiConfigAdminPage: React.FC = () => {
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");

  const [config, setConfig] = useState<GlobalAiConfig>(defaultConfig);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMessage, setConfigMessage] = useState<string>("");

  const [history, setHistory] = useState<AotdListItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAotdId, setSelectedAotdId] = useState<string>("");

  const [details, setDetails] = useState<AotdDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const pipeline = useMemo(
    () => details?.pipelineMetadata ?? details?.metadata?.pipeline ?? null,
    [details]
  );

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    setConfigMessage("");
    try {
      const res = await apiClient.get("/api/ai/admin/config");
      setConfig({
        smallModel: res.data?.smallModel ?? "",
        largeModel: res.data?.largeModel ?? "",
        veryLargeModel: res.data?.veryLargeModel ?? "",
        imageModel: res.data?.imageModel ?? "",
      });
    } catch (error: any) {
      setConfigMessage(error?.response?.data?.message ?? "failed to load ai config");
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const fetchHistory = useCallback(async (nextPage: number) => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await apiClient.get("/api/art-of-the-day/admin", {
        params: { page: nextPage, limit: 15 },
      });
      const arts: AotdListItem[] = res.data?.arts ?? [];
      const pages = res.data?.pagination?.pages ?? 1;

      setHistory(arts);
      setTotalPages(Math.max(1, pages));
      if (arts.length > 0) {
        setSelectedAotdId((prev) => prev || arts[0]._id);
      } else {
        setSelectedAotdId("");
      }
    } catch (error: any) {
      setHistoryError(error?.response?.data?.message ?? "failed to load aotd history");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchDetails = useCallback(async (id: string) => {
    if (!id) return;
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const res = await apiClient.get(`/api/art-of-the-day/admin/${id}`);
      setDetails(res.data);
    } catch (error: any) {
      setDetails(null);
      setDetailsError(error?.response?.data?.message ?? "failed to load aotd details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchHistory(page);
  }, [fetchHistory, page]);

  useEffect(() => {
    if (selectedAotdId) {
      fetchDetails(selectedAotdId);
    } else {
      setDetails(null);
    }
  }, [fetchDetails, selectedAotdId]);

  if (user.bootstrapStatus !== "ready") {
    return null;
  }

  if (!isAdmin) {
    return <Navigate to="/user-stats" replace />;
  }

  const onSave = async () => {
    setSavingConfig(true);
    setConfigMessage("");
    try {
      const res = await apiClient.put("/api/ai/admin/config", config);
      const updated = res.data?.config ?? config;
      setConfig({
        smallModel: updated.smallModel ?? "",
        largeModel: updated.largeModel ?? "",
        veryLargeModel: updated.veryLargeModel ?? "",
        imageModel: updated.imageModel ?? "",
      });
      setConfigMessage("ai config saved");
    } catch (error: any) {
      setConfigMessage(error?.response?.data?.message ?? "failed to save ai config");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <Page className="aic-page">
      <style>{`
        @media (max-width: 700px) {
          .aic-page { padding: 12px 8px !important; }
          .aic-row { flex-wrap: wrap !important; gap: 8px !important; }
          .aic-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .aic-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
          .aic-grid-1 { grid-template-columns: 1fr !important; }
          .aic-card-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important; }
          .aic-textarea-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; min-height: 120px; }
          .aic-pre-wrap { white-space: pre-wrap !important; word-break: break-word !important; }
        }
      `}</style>
      <Title>AI Config</Title>
      <Subtitle>
        configure global ai model defaults and inspect previous aotd generation flows.
      </Subtitle>

      <Layout className="aic-grid-1">
        <Card>
          <h2 style={{ marginTop: 0 }}>Global models</h2>
          {loadingConfig ? (
            <StatusText>loading config...</StatusText>
          ) : (
            <>
              <FieldLabel htmlFor="smallModel">smallModel</FieldLabel>
              <Input
                id="smallModel"
                className="aic-input-full"
                value={config.smallModel}
                onChange={(e) => setConfig((prev) => ({ ...prev, smallModel: e.target.value }))}
              />

              <FieldLabel htmlFor="largeModel">largeModel</FieldLabel>
              <Input
                id="largeModel"
                className="aic-input-full"
                value={config.largeModel}
                onChange={(e) => setConfig((prev) => ({ ...prev, largeModel: e.target.value }))}
              />

              <FieldLabel htmlFor="veryLargeModel">veryLargeModel</FieldLabel>
              <Input
                id="veryLargeModel"
                className="aic-input-full"
                value={config.veryLargeModel}
                onChange={(e) => setConfig((prev) => ({ ...prev, veryLargeModel: e.target.value }))}
              />

              <FieldLabel htmlFor="imageModel">imageModel</FieldLabel>
              <Input
                id="imageModel"
                list="image-model-options"
                aria-describedby="image-model-help"
                className="aic-input-full"
                value={config.imageModel}
                onChange={(e) => setConfig((prev) => ({ ...prev, imageModel: e.target.value }))}
              />
              <datalist id="image-model-options">
                <option value="gpt-image-2.5-flare">GPT Image 2.5 Flare: recommended for everyday generation</option>
                <option value="gpt-image-2.5-sunburst">GPT Image 2.5 Sunburst: most capable generation and editing</option>
                <option value="gpt-image-2">GPT Image 2</option>
              </datalist>
              <SmallText id="image-model-help">
                Choose Flare for fast, high-quality generation or Sunburst for demanding images and precise edits.
                You can also enter a model ID. Save to apply it to future AOTD generations.
              </SmallText>

              <Row className="aic-row">
                <Button onClick={onSave} disabled={savingConfig}>
                  {savingConfig ? "saving..." : "save"}
                </Button>
                <GhostButton onClick={fetchConfig} disabled={savingConfig}>
                  reload
                </GhostButton>
              </Row>

              {configMessage ? <StatusText>{configMessage}</StatusText> : null}
            </>
          )}

          <h3 style={{ marginTop: 22 }}>AOTD history</h3>
          <SmallText>select an entry to inspect full pipeline data</SmallText>

          <Row className="aic-row">
            <GhostButton
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={historyLoading || page <= 1}
            >
              prev
            </GhostButton>
            <GhostButton
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={historyLoading || page >= totalPages}
            >
              next
            </GhostButton>
            <SmallText>
              page {page} / {totalPages}
            </SmallText>
          </Row>

          {historyLoading ? <StatusText>loading history...</StatusText> : null}
          {historyError ? <StatusText>{historyError}</StatusText> : null}

          <List>
            {history.map((item) => (
              <ListItem
                key={item._id}
                active={item._id === selectedAotdId}
                onClick={() => setSelectedAotdId(item._id)}
              >
                <div><strong>{item.title || "(untitled)"}</strong></div>
                <SmallText>{formatDate(item.date)}</SmallText>
                <SmallText>
                  by {item.createdBy || "unknown"} | {item.generationStatus?.status || "unknown"}
                </SmallText>
              </ListItem>
            ))}
          </List>
        </Card>

        <Card>
          <h2 style={{ marginTop: 0 }}>AOTD full pipeline</h2>
          {detailsLoading ? <StatusText>loading details...</StatusText> : null}
          {detailsError ? <StatusText>{detailsError}</StatusText> : null}

          {!detailsLoading && !details && !detailsError ? (
            <EmptyState>select an aotd from the list to inspect its flow.</EmptyState>
          ) : null}

          {details ? (
            <>
              <KeyValue><strong>date:</strong> {formatDate(details.date)}</KeyValue>
              <KeyValue><strong>title:</strong> {details.title || "(untitled)"}</KeyValue>
              <KeyValue><strong>createdBy:</strong> {details.createdBy || "unknown"}</KeyValue>
              <KeyValue><strong>status:</strong> {details.generationStatus?.status || "unknown"}</KeyValue>
              <KeyValue><strong>state:</strong> {details.generationStatus?.state || "n/a"}</KeyValue>
              <KeyValue><strong>imageModel:</strong> {details.imageModel || "n/a"}</KeyValue>
              <KeyValue><strong>prompt:</strong> {details.prompt || "n/a"}</KeyValue>

              {pipeline ? (
                <>
                  {pipeline.emojis && pipeline.emojis.length > 0 ? (
                    <KeyValue><strong>emojis:</strong> {pipeline.emojis.join(" ")}</KeyValue>
                  ) : null}
                  <KeyValue><strong>concept:</strong> {pipeline.concept || "n/a"}</KeyValue>
                  <KeyValue><strong>artStyle:</strong> {pipeline.artStyle || "n/a"}</KeyValue>
                  <KeyValue><strong>critique:</strong> {pipeline.critique || "n/a"}</KeyValue>
                  <KeyValue><strong>imagePrompt:</strong> {pipeline.imagePrompt || "n/a"}</KeyValue>
                  <KeyValue><strong>totalDurationMs:</strong> {pipeline.totalDurationMs ?? "n/a"}</KeyValue>

                  <h3 style={{ marginTop: 16 }}>Steps (input / output per model)</h3>
                  {pipeline.steps && pipeline.steps.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {pipeline.steps.map((step, idx) => (
                        <StepBlock key={`${step.name || "step"}-${idx}`}>
                          <StepHeader className="aic-row">
                            <strong>{step.name || "step"}</strong>
                            <StepDetail>{step.model || "—"}</StepDetail>
                            <StepDetail>{step.durationMs != null ? `${step.durationMs}ms` : "—"}</StepDetail>
                          </StepHeader>
                          <details>
                            <StepInputSummary>Input (prompt to model)</StepInputSummary>
                            <StepInputBody className="aic-pre-wrap">{step.promptSent || "—"}</StepInputBody>
                          </details>
                          <StepOutputLabel>Output</StepOutputLabel>
                          <StepOutputBody className="aic-pre-wrap">{step.rawResponse || "—"}</StepOutputBody>
                        </StepBlock>
                      ))}
                    </div>
                  ) : (
                    <EmptyState>no pipeline steps captured on this record.</EmptyState>
                  )}
                </>
              ) : (
                <EmptyState>no pipeline metadata found on this record.</EmptyState>
              )}
            </>
          ) : null}
        </Card>
      </Layout>
    </Page>
  );
};

export default AiConfigAdminPage;
